import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthModalService } from "src/app/Services/auth-modal.service";
import {
  PlanConfigModel,
  SubscriptionService,
} from "src/app/Services/subscription.service";
import {
  getTrialConfig,
  growthCoinsFeature,
  growthGuideFeature,
  growthViewsFeature,
  mergePlanConfig,
  starterCoinsFeature,
  starterGuideFeature,
  starterViewsFeature,
} from "src/app/shared/utils/plan-config.util";
import { setCoinPurchaseReturnUrl } from "src/app/shared/utils/coin-purchase-return.util";
import { isTrialActive, isTrialEligible } from "src/app/shared/utils/trial.util";

export interface UpgradePlanDialogData {
  returnUrl?: string;
  isAnnual?: boolean;
  /** When set, shows context-specific upgrade copy (e.g. advanced-access). */
  feature?: string;
}

interface UpgradePlanCard {
  id: "starter" | "growth" | "enterprise";
  name: string;
  monthlyPrice: number | null;
  audience: string;
  features: string[];
  cta: string;
  featured?: boolean;
  /** Starter only: checkout opens the 14-day free trial. */
  startsTrial?: boolean;
}

@Component({
  selector: "app-upgrade-plan-dialog",
  templateUrl: "./upgrade-plan-dialog.component.html",
  styleUrls: ["./upgrade-plan-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradePlanDialogComponent implements OnInit, OnDestroy {
  @Input() inlineData: UpgradePlanDialogData | null = null;
  @Output() inlineClose = new EventEmitter<void>();

  isAnnual = false;
  isLoggedIn = false;
  returnUrl: string | null = null;
  feature: string | null = null;
  planConfig: PlanConfigModel = mergePlanConfig();
  plansList: UpgradePlanCard[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional()
    private dialogRef: MatDialogRef<UpgradePlanDialogComponent> | null,
    private router: Router,
    private authModalService: AuthModalService,
    private subscriptionService: SubscriptionService,
    private cdr: ChangeDetectorRef,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data: UpgradePlanDialogData | null
  ) {}

  ngOnInit(): void {
    this.rebuildPlansList();

    this.subscriptionService
      .getPlanConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (config) => {
          this.planConfig = mergePlanConfig(config);
          this.rebuildPlansList();
          this.cdr.markForCheck();
        },
        error: () => {
          this.planConfig = mergePlanConfig();
          this.rebuildPlansList();
          this.cdr.markForCheck();
        },
      });

    const user = JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    this.isLoggedIn = !!user;
    const config = this.inlineData || this.data || {};
    this.isAnnual = !!config.isAnnual;
    this.returnUrl = config.returnUrl || null;
    this.feature = config.feature || null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByPlanId(_index: number, plan: UpgradePlanCard): string {
    return plan.id;
  }

  trackByFeature(_index: number, feature: string): string {
    return feature;
  }

  private rebuildPlansList(): void {
    const trial = getTrialConfig(this.planConfig);
    const loggedIn = !!localStorage.getItem("LoggedInUser");
    const offerTrial = !isTrialActive() && (!loggedIn || isTrialEligible());
    const starterFeatures = [
      starterGuideFeature(this.planConfig),
      starterViewsFeature(this.planConfig),
      starterCoinsFeature(this.planConfig),
    ];
    if (offerTrial) {
      starterFeatures.unshift(`${trial.days}-day free trial (${trial.maxGuides} guide, manual creation)`);
    }

    this.plansList = [
      {
        id: "starter",
        name: "Starter",
        monthlyPrice: 99,
        audience: "Early-stage brands or pilot projects",
        features: starterFeatures,
        cta: offerTrial
          ? `Start ${trial.days}-day free trial`
          : isTrialActive()
            ? "Upgrade to paid Starter now"
            : "Subscribe to Starter",
        startsTrial: offerTrial,
      },
      {
        id: "growth",
        name: "Growth",
        monthlyPrice: 399,
        audience: "Established DTC brands or retailers",
        features: [
          growthGuideFeature(this.planConfig),
          growthViewsFeature(this.planConfig),
          growthCoinsFeature(this.planConfig),
        ],
        cta: "Subscribe to Growth",
        featured: true,
      },
      {
        id: "enterprise",
        name: "Enterprise",
        monthlyPrice: null,
        audience: "Large retailers & manufacturers",
        features: [
          "Unlimited guides + steps",
          "Custom SLAs & Support",
          "Custom AI coin allowance + top-ups",
        ],
        cta: "Contact Sales",
      },
    ];
  }

  private dismiss(result?: unknown): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
      return;
    }
    this.inlineClose.emit();
  }

  get dialogSubtitle(): string {
    if (this.feature === "advanced-access") {
      return "Upgrade to Growth or Enterprise to use IP whitelist, geo-fence, device lock, and company email domain controls on your guides.";
    }
    if (this.feature === "creator-publish-access") {
      return "Upgrade to Starter or Growth to publish private, link-only, or restricted content. Creator plan supports public paid guides only.";
    }
    if (this.feature === "trial-ai") {
      return "AI creation isn't included in the Starter free trial. Upgrade now to unlock Smart Create, Flowchest AI and AI writing help.";
    }
    if (this.feature === "trial-guide-packs") {
      return "Guide packs aren't included in the Starter free trial. Upgrade now to bundle your guides into packs.";
    }
    if (this.feature === "trial-guides") {
      return "You've used the guide included in your Starter free trial. Upgrade now to keep creating.";
    }
    if (this.feature === "trial-org") {
      return "Organizations unlock once your Starter subscription is paid. Upgrade now to set up your organization.";
    }
    return "Subscribe to a business plan to publish free guides, unlock higher limits, and get a monthly AI coin allowance.";
  }

  toggleBilling(): void {
    this.isAnnual = !this.isAnnual;
    this.cdr.markForCheck();
  }

  getPlanAmount(plan: UpgradePlanCard): number {
    if (plan.monthlyPrice == null) {
      return 0;
    }
    return this.isAnnual
      ? Math.round(plan.monthlyPrice * 0.8)
      : plan.monthlyPrice;
  }

  close(): void {
    this.dismiss();
  }

  goToPurchaseCoins(): void {
    const returnUrl = this.returnUrl || this.router.url;
    setCoinPurchaseReturnUrl(returnUrl);
    this.dismiss();
    this.router.navigate(["/pricing"], {
      queryParams: { buyCoins: "1", returnUrl },
      fragment: "ai-coins",
    });
  }

  selectPlan(plan: UpgradePlanCard): void {
    if (plan.id === "enterprise") {
      this.dismiss({ selected: plan.id });
      this.router.navigate(["/contact-us"]);
      return;
    }

    const queryParams: { [key: string]: string } = {
      plan: plan.id,
      billing: this.isAnnual ? "annual" : "monthly",
    };
    if (this.returnUrl) {
      queryParams.returnUrl = this.returnUrl;
    }
    if (plan.startsTrial) {
      queryParams.trial = "1";
    }

    if (!this.isLoggedIn) {
      this.dismiss();
      const target = `/subscriptions?${new URLSearchParams(
        queryParams
      ).toString()}`;
      this.authModalService.openModal("registration", target);
      return;
    }

    this.dismiss({ selected: plan.id });
    void this.router.navigateByUrl(
      `/subscriptions?${new URLSearchParams(queryParams).toString()}`
    );
  }
}
